import { Component, OnInit } from "@angular/core";
import { Ng4LoadingSpinnerService } from "ng4-loading-spinner";
import { NotificationsService } from "angular2-notifications";
import { ColDef, GridApi } from "ag-grid-community";
import { UserService } from "../services/user.service";
import { OfframpService } from "../services/offramp.service";
import * as moment from "moment";

const notif = { timeOut: 3000, showProgressBar: true, pauseOnHover: false, clickToClose: true, maxLength: 80 };

// Payout daily metrics for one IST day: the payout orders created that day as the client saw them (Client View),
// every attempt of them by gateway + MID (Gateway View), and the checks that both give the same totals.
@Component({
  selector: "app-payout-daily-metrics",
  templateUrl: "./payout-daily-metrics.component.html",
  styleUrls: ["./payout-daily-metrics.component.css"],
})
export class PayoutDailyMetricsComponent implements OnInit {
  _csrf: string;
  date: Date = new Date();
  userId = "";
  gateway = "";
  userList: any[] = [];
  gatewayOptions: any[] = [];
  metrics: any = null;

  private clientGridApi: GridApi;
  private gatewayGridApi: GridApi;
  private issuesGridApi: GridApi;

  // Compact grids: fixed widths per kind of column (numbers right aligned), short headers with the full name on hover
  defaultColDef: ColDef = { sortable: true, filter: true, resizable: true, minWidth: 56, width: 80, cellClass: "compact-cell", headerClass: "compact-header" };
  private count = (headerName: string, field: string, headerTooltip?: string): ColDef =>
    ({ headerName, field, headerTooltip: headerTooltip || headerName, width: 70, type: "numericColumn" });
  private amount = (headerName: string, field: string, headerTooltip?: string): ColDef =>
    ({ headerName, field, headerTooltip: headerTooltip || headerName, width: 112, type: "numericColumn", valueFormatter: (p) => this.inr(p.value) });
  private time = (headerName: string, getter: (data: any) => any, headerTooltip?: string): ColDef =>
    ({ headerName, headerTooltip: headerTooltip || headerName, width: 74, type: "numericColumn", valueGetter: (p) => getter(p.data), valueFormatter: (p) => this.duration(p.value) });
  private rate = (headerName: string, field: string, headerTooltip?: string): ColDef =>
    ({ headerName, field, headerTooltip: headerTooltip || headerName, width: 78, type: "numericColumn", valueFormatter: (p) => this.percent(p.value) });

  clientColumns: ColDef[] = [
    { headerName: "Client", field: "ClientName", width: 190, pinned: "left", tooltipField: "ClientName" },
    this.count("Orders", "Orders"),
    this.amount("Amount", "Amount"),
    this.count("Done", "Completed", "Completed"),
    this.amount("Done ₹", "CompletedAmount", "Completed amount"),
    this.count("Failed", "Failed"),
    this.count("Rej.", "Rejected", "Rejected"),
    this.count("In Prog.", "InProgress", "In progress"),
    this.rate("Success", "SuccessRate", "Success % (completed / completed + failed)"),
    this.count("Retried", "Retried", "Orders that needed more than one attempt"),
    this.time("Wait p50", (d) => d.Wait.p50, "Wait p50: first sent to a gateway -> completed"),
    this.time("Wait p90", (d) => d.Wait.p90, "Wait p90: first sent to a gateway -> completed"),
    this.time("Wait p95", (d) => d.Wait.p95, "Wait p95: first sent to a gateway -> completed"),
    this.time("Wait max", (d) => d.Wait.max, "Wait max: first sent to a gateway -> completed"),
    this.time("Total p95", (d) => d.Total.p95, "Total p95: order received -> completed"),
  ];

  gatewayColumns: ColDef[] = [
    { headerName: "Gateway", field: "Label", width: 230, pinned: "left", tooltipField: "Label" },
    this.count("Sent", "Sent", "Attempts sent to the gateway"),
    this.amount("Amount", "Amount"),
    this.count("Done", "Completed", "Completed"),
    this.amount("Done ₹", "CompletedAmount", "Completed amount"),
    this.count("Failed", "Failed"),
    this.count("In Prog.", "InProgress", "In progress"),
    this.rate("Success", "SuccessRate", "Success % (completed / completed + failed)"),
    this.count("Retr. Else", "RetriedElsewhere", "Failed here, then sent to another gateway"),
    this.time("Time p50", (d) => d.CompletedTime.p50, "Time p50: attempt sent -> completed"),
    this.time("Time p90", (d) => d.CompletedTime.p90, "Time p90: attempt sent -> completed"),
    this.time("Time p95", (d) => d.CompletedTime.p95, "Time p95: attempt sent -> completed"),
    this.time("Time max", (d) => d.CompletedTime.max, "Time max: attempt sent -> completed"),
    this.time("Fail p95", (d) => d.FailedTime.p95, "Fail p95: attempt sent -> failed"),
    this.count("Est.", "Estimated", "Retry attempts timed from the previous attempt (before per-attempt send time was recorded)"),
    {
      headerName: "Top Failure Reasons", width: 320, minWidth: 200,
      valueGetter: (p) => (p.data.TopFailureReasons || []).map((r) => `${r.Reason} (${r.Count})`).join(", "),
      tooltipValueGetter: (p) => p.value,
    },
  ];

  checkColumns: ColDef[] = [
    { headerName: "", field: "Ok", width: 44, minWidth: 44, valueFormatter: (p) => (p.value ? "✅" : "❌") },
    { headerName: "Check", field: "Check", width: 360, tooltipField: "Check" },
    { headerName: "Client View", field: "Client", width: 110, type: "numericColumn" },
    { headerName: "Gateway View", field: "Gateway", width: 115, type: "numericColumn" },
    { headerName: "Flagged", field: "Issues", headerTooltip: "Orders flagged", width: 80, type: "numericColumn", valueFormatter: (p) => (p.value === undefined ? "" : p.value) },
  ];

  issueColumns: ColDef[] = [
    { headerName: "Order Id", field: "OrderId", width: 200 },
    { headerName: "Client Order Id", field: "ClientOrderId", width: 170, tooltipField: "ClientOrderId" },
    { headerName: "Client", field: "Client", width: 160, tooltipField: "Client" },
    { headerName: "Check", field: "Check", width: 260, tooltipField: "Check" },
    { headerName: "Detail", field: "Detail", width: 300, tooltipField: "Detail" },
  ];

  constructor(
    private notifSer: NotificationsService,
    private userService: UserService,
    private offrampService: OfframpService,
    private spinnerService: Ng4LoadingSpinnerService
  ) { }

  ngOnInit() {
    this.userService.csrf().subscribe(
      (success) => {
        this._csrf = success._csrf;
        this.loadClients();
        this.load();
      },
      (error) => this.notifSer.error("Error", (error.error && error.error.message) || "Failed to start", notif)
    );
  }

  loadClients() {
    this.offrampService.getOfframpCustomerList({ _csrf: this._csrf, perpage: 10000, page: 1, search: "", status: "Active" }).subscribe(
      (response: any) => {
        this.userList = (response && (response.customers || response.data)) || (Array.isArray(response) ? response : []);
      },
      () => (this.userList = [])
    );
  }

  load() {
    if (!this.date) {
      this.notifSer.error("Error", "Select a date", notif);
      return;
    }
    this.spinnerService.show();
    this.offrampService.getPayoutDailyMetrics({
      _csrf: this._csrf,
      date: moment(this.date).format("YYYY-MM-DD"),
      userId: this.userId,
      gateway: this.gateway,
    }).subscribe(
      (response: any) => {
        this.spinnerService.hide();
        if (!response || !response.status) {
          this.notifSer.error("Error", (response && response.message) || "Failed to load metrics", notif);
          return;
        }
        this.metrics = response.data;
        this.gatewayOptions = response.data.gatewayOptions || [];
      },
      (error) => {
        this.spinnerService.hide();
        this.notifSer.error("Error", (error.error && error.error.message) || "Failed to load metrics", notif);
      }
    );
  }

  onClientGridReady(params) { this.clientGridApi = params.api; }
  onGatewayGridReady(params) { this.gatewayGridApi = params.api; }
  onIssuesGridReady(params) { this.issuesGridApi = params.api; }

  export(view: string) {
    const day = this.metrics ? this.metrics.date : moment(this.date).format("YYYY-MM-DD");
    const api = view === "client" ? this.clientGridApi : view === "gateway" ? this.gatewayGridApi : this.issuesGridApi;
    if (api) {
      api.exportDataAsCsv({ fileName: `payout-${view}-metrics-${day}.csv`, processCellCallback: (p) => this.exportValue(p) });
    }
  }

  // Exported cells use the formatted text (durations, amounts) shown in the grid
  private exportValue(params) {
    const colDef: any = params.column.getColDef();
    return colDef.valueFormatter ? colDef.valueFormatter({ value: params.value, data: params.node.data }) : params.value;
  }

  inr(value) {
    return value === null || value === undefined ? "" : Number(value).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  }

  percent(value) {
    return value === null || value === undefined ? "-" : `${value}%`;
  }

  // Seconds as "45s", "3m 05s", "1h 02m"
  duration(seconds) {
    if (seconds === null || seconds === undefined) return "-";
    const s = Number(seconds);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
    return `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}m`;
  }
}



// WEBPACK FOOTER //
// ./src/app/payout-daily-metrics/payout-daily-metrics.component.ts